import { Environment, Network, RecordSource, Store } from 'relay-runtime';

type GraphError = {
  message: string;
  type?: string;
};

export class FetchError extends Error {
  public type: string | boolean = false;
  constructor(error: GraphError) {
    super(error.message); // (1)
    this.type = error?.type || false; // (2)
  }
}

/**
 * A single GraphQL error has a `path` only when it relates to a specific
 * field. A request that was rejected outright (no valid session) errors at
 * the root, so it has no path. We only treat root-level errors as fatal —
 * a field-level permission denial is a partial result, not a session failure.
 */
function isRootLevelError(err: any): boolean {
  return !err?.path || err.path.length === 0;
}

function readCookie(name: string) {
  var nameEQ = name + '=';
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

async function fetchQuery(operation: any, variables: any) {
  const CSRF_TOKEN = readCookie('csrf');

  const headers: any = {
    'content-type': 'application/json',
    'X-CSRF-TOKEN': CSRF_TOKEN
  };

  return fetch(`//${process.env.REACT_APP_API_DOMAIN}/graphql`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query: operation.text,
      variables
    }),
    credentials: 'include'
  }).then(async (response) => {
    const json = await response.json();

    if (json && 'errors' in json && json.errors?.length) {
      // A root-level error means the whole operation was rejected (e.g. no
      // valid session). Throw so callers can redirect to /login.
      const rootError = json.errors.find(isRootLevelError);
      const noData = json.data == null;

      if (rootError || noData) {
        const err = rootError ?? json.errors[0];
        throw new FetchError({
          message: err?.extensions?.message || err?.message,
          type: err?.extensions?.type
        });
      }

      // Otherwise this is a partial response: some fields resolved and one or
      // more field-level errors are attached. Hand the whole payload (data +
      // errors) back to Relay so the page still renders and components can
      // show an inline "no access" state for the missing field.
    }

    return json;
  });
}

/**
 * Performs a raw GraphQL request against the API, reusing the same credentials
 * and CSRF handling as the Relay network layer, but WITHOUT the error-throwing
 * wrapper. Used for lightweight imperative checks that handle their own errors.
 */
async function rawGraphQL(query: string, variables?: any): Promise<any> {
  const CSRF_TOKEN = readCookie('csrf');
  const response = await fetch(
    `//${process.env.REACT_APP_API_DOMAIN}/graphql`,
    {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'X-CSRF-TOKEN': CSRF_TOKEN
      } as any,
      body: JSON.stringify({ query, variables }),
      credentials: 'include'
    }
  );
  return response.json();
}

/**
 * Checks the current session via `user(noError: true)`, which
 * returns `null` (rather than erroring) when there is no valid session, so a
 * network/permission failure on some other query can't be mistaken for being
 * logged out.
 */
export async function checkSession(): Promise<string | null> {
  try {
    const json = await rawGraphQL(`
      query Environment_IsLoggedIn_Query {
        user(noError: true) {
          type
        }
      }
    `);
    return json?.data?.user?.type;
  } catch {
    return null;
  }
}

/**
 * Convenience wrapper: true only when the session is a confirmed logged-in
 * manager. Treats 'unknown' (failed check) as not-logged-in — use checkSession
 * directly when you need to distinguish a network failure from another user.
 */
export async function isLoggedInManager(): Promise<boolean> {
  return (await checkSession()) === 'manager';
}

const environment = new Environment({
  network: Network.create(fetchQuery),
  store: new Store(new RecordSource())
});

export default environment;
